import type * as React from 'react'
import { Popover } from '@buffer-mono/popcorn'

import { getFragmentData, type FragmentType } from '~publish/gql'

import { CommentsPostCard_Post } from '../fragments/CommentsPostCard_Post'
import { useLoadCommentPostCard } from '../hooks/useLoadCommentPostCard'
import { CommentsPostCard } from '../CommentsPostCard'
import { useDelayedHover } from '../hooks/useDelayedHover'

import styles from './CommentsPostPopover.module.css'

type CommentsPostPopoverProps = {
  children: React.ReactNode
  post: FragmentType<typeof CommentsPostCard_Post>
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

export const CommentsPostPopover = ({
  children,
  post: postFragment,
  isOpen,
  onOpenChange,
}: CommentsPostPopoverProps): JSX.Element => {
  const commentPost = getFragmentData(CommentsPostCard_Post, postFragment)
  const { data } = useLoadCommentPostCard(commentPost.id, {
    skip: !isOpen,
  })
  const { handleMouseEnter, handleMouseLeave } = useDelayedHover({
    onOpen: () => onOpenChange(true),
    onClose: () => onOpenChange(false),
    isOpen,
    openDelayDuration: 200,
  })

  const closePopoverWhenComposerOpensOrLinkClicks = (
    evt: React.MouseEvent<HTMLElement>,
  ): void => {
    // If the click is on a link, close the popover
    if (evt.target instanceof HTMLAnchorElement) {
      onOpenChange(false)
      return
    }

    // Look for the closest ancestor that has the aria-controls attribute set to "composer-root"
    const openComposerTrigger = (evt.target as Element)?.closest(
      '[aria-controls="composer-root"]',
    )
    if (openComposerTrigger) {
      onOpenChange(false)
    }
  }

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </Popover.Trigger>
      <Popover.Content
        side="left"
        align="start"
        className={styles.popoverContent}
        onClick={closePopoverWhenComposerOpensOrLinkClicks}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <CommentsPostCard post={data?.post} partialData={postFragment} />
      </Popover.Content>
    </Popover>
  )
}
